import './BookReadStyle.css';
import { ReactNode } from "react";
import { DictionaryLink, Language, Sentence } from "../../../rest/models";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { emptySentencePlaceholder } from "../utils";
import { RenderDictionaryLink } from "./RenderDictionaryLink";
import { createSegmentEndpoint, createSegments, SpeakPlaylistManager } from "./SpeakPlaylistManager";
import { bookReaderActions } from "./slice";

export const selectedWordClass = 'ernots_SelectedWord';

export function RenderSentenceTranslation(props: SentenceProps)
{
    const l = props.language;
    const s = props.sentence;
    const d = props.dictLinks;

    const isSource = useAppSelector(state => state.bookReader.book.source_language === l.id);
    const legend = l.name + (isSource ? ' (Source)' : '');

    const dlsForLangRow = (d?.length && s.type !== 'n')
        ? <div>{d.map(dl => <RenderDictionaryLink key={dl.key} dl={dl}/>)}</div>
        : <></>;

    let trans = useAppSelector(state => state.bookReader.translationsByLangAndSentence[l.id + '/' + s.sentence_id]) || '';
    if (s.text === emptySentencePlaceholder)
    {
        trans = '';
    }
    
    const audioSegments = createSegments(trans);
    const audioEndpoints = audioSegments.map(seg => createSegmentEndpoint(seg, l.gtrans_code));
    
    return (
        <fieldset>
            <legend>{legend}</legend>
            <div><ClickableWords text={trans} sentenceId={s.id} langId={l.id}/></div>
            {dlsForLangRow}
            <SpeakPlaylistManager fileEndpoints={audioEndpoints}/>
        </fieldset>);
}

interface SentenceProps
{
    language: Language;
    sentence: Sentence;
    dictLinks: DictionaryLink[];
}

const letters = '\\p{L}';
const apostrophes = "'’ʼʾʻ′‵＇";

function ClickableWords({text, sentenceId, langId}: WordsProps)
{
    const l_and_a = letters + apostrophes;

    const not_l_or_a = `[^${l_and_a}]`;
    const double_apostrophes = `(?:${apostrophes}){2,}`;

    /*
        Matches any sequence of the following:
            Characters that are neither letters nor apostrophes
            Sequences of two or more apostrophes
            End of string ($)
    */
    const reSpacesBetweenWords = RegExp(`(${not_l_or_a}|${double_apostrophes})+|$`, 'gui');
    const matches = text.matchAll(reSpacesBetweenWords);

    const ret = [] as ReactNode[];
    let prevMatchStringEnd = 0;
    matches.forEach(function(m, i)
    {
        const word = text.substring(prevMatchStringEnd, m.index);
        if (word)
        {
            ret.push(<ClickableWord
                key={i}
                wordId={i}
                word={word}
                sentenceId={sentenceId}
                langId={langId}/>);
        }
        if (m[0])
        {
            ret.push(m[0]);
        }

        prevMatchStringEnd = m.index + m[0].length;
    });

    return ret;
}

interface WordsProps
{
    text: string;
    sentenceId: string;
    langId: string;
}

function ClickableWord({word, wordId, sentenceId, langId}: WordProps)
{
    const appDispatch = useAppDispatch();
    const wordClass = useAppSelector(function(state)
    {
        const sw = state.bookReader.selectedWord;
        const selected = sw.sentenceId === sentenceId
            && sw.langId === langId
            && sw.wordId === wordId
            && sw.lettersId === -1;
        return selected ? selectedWordClass : '';
    });

    const matches = word.matchAll(RegExp(`[${apostrophes}]+|$`, 'gui')).toArray();

    const children = [] as ReactNode[];
    let prevMatchStringEnd = 0;
    matches.forEach(function(m, i)
    {
        const apostrophe = m[0];
        const letters = word.substring(prevMatchStringEnd, m.index);
        if (letters)
        {
            children.push(<ClickableLetters
                key={i}
                lettersId={i}
                wordId={wordId}
                sentenceId={sentenceId}
                langId={langId}
                letters={letters}/>
            );
        }
        if (apostrophe)
        {
            children.push(apostrophe);
        }

        prevMatchStringEnd = m.index + m[0].length;
    });

    return <span
        children={children}
        className={wordClass}
        onClick={function(e)
        {
            e.stopPropagation();
            appDispatch(bookReaderActions.setSelectedWord({sentenceId, langId, wordId, lettersId: -1}));
        }}/>
}

interface WordProps
{
    word: string;
    wordId: number;
    sentenceId: string;
    langId: string;
}

function ClickableLetters({letters, lettersId, wordId, sentenceId, langId}: LettersProps)
{
    const appDispatch = useAppDispatch();
    const lettersClass = useAppSelector(function(state)
    {
        const sw = state.bookReader.selectedWord;
        const selected = sw.sentenceId === sentenceId
            && sw.langId === langId
            && sw.wordId === wordId
            && sw.lettersId === lettersId;

        return selected ? selectedWordClass : '';
    });

    return <span
        children={letters}
        className={lettersClass}
        onClick={function(e)
        {
            e.stopPropagation();
            appDispatch(bookReaderActions.setSelectedWord({sentenceId, langId, wordId, lettersId}));
        }}/>
}

interface LettersProps
{
    letters: string;
    lettersId: number;
    wordId: number;
    sentenceId: string;
    langId: string;
}